import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { PESTS_COVERED_STATUS } from '@ps/common-features/constants/pests'
import { Alert } from '@ps/ds/components/primitives/Alert/Alert'
import { Button } from '@ps/ds/components/primitives/Button/Button'
import { Hourglass } from '@ps/ds/icons/Hourglass'
import { ICON_GRADIENTS } from '@ps/ds/icons/constants/gradients'
import { getGradientUrl, renderGradientDef } from '@ps/ds/icons/helpers/renderGradientDef'
import { REQUEST_STATUS } from 'src/api/constants/request'
import type {
  TrackingPest,
  TrackingRequestStatus,
} from 'src/api/domains/service-tracking/serviceTracking.types'
import { NS } from 'src/constants/i18n'

type RequestAlertProps = {
  trackingPests?: TrackingPest[]
  requestStatus?: TrackingRequestStatus
  denyReason?: string | null
}

const REVIEW_PEST_STATUSES: string[] = [
  PESTS_COVERED_STATUS.RED_BUCKET,
  PESTS_COVERED_STATUS.QUOTE_PESTSHARE,
  PESTS_COVERED_STATUS.NEEDS_ADDITIONAL_INFO,
]

export const RequestAlert = ({ trackingPests, requestStatus, denyReason }: RequestAlertProps) => {
  const { t } = useTranslation(NS.TRACKING)
  const [showMore, setShowMore] = useState(false)

  if (!trackingPests?.length && !requestStatus) {
    return null
  }

  if (requestStatus?.slug === REQUEST_STATUS.DENIED) {
    const description = denyReason ? (
      <Trans
        i18nKey={`${NS.TRACKING}:alert.voided.description`}
        values={{ denyReason }}
        components={{
          paragraph: <p className="mb-2" />,
          emphasis: <em />,
        }}
      />
    ) : (
      t('alert.voided.descriptionWithoutNotes')
    )

    return <Alert title={t('alert.voided.title')} description={description} variant="error" />
  }

  if (trackingPests?.some((pest) => pest.status.slug === PESTS_COVERED_STATUS.NEEDS_INFO_TENANT)) {
    return (
      <Alert
        title={t('alert.needsInfoTenant.title')}
        description={t('alert.needsInfoTenant.description')}
        variant="tip"
      />
    )
  }

  if (requestStatus?.slug === REQUEST_STATUS.DELAYED) {
    return (
      <Alert
        title={t('alert.delayed.title')}
        icon={
          <>
            {renderGradientDef('Hourglass', ICON_GRADIENTS.yellow)}
            <Hourglass fill={getGradientUrl('Hourglass', ICON_GRADIENTS.yellow.id)} />
          </>
        }
        description={
          <>
            <p className="mb-2">{t('alert.delayed.description')}</p>
            {showMore && (
              <div>
                <Trans
                  i18nKey={`${NS.TRACKING}:alert.delayed.descriptionExpanded`}
                  components={{
                    paragraph: <p className="mb-2" />,
                    list: <ul className="list-disc pl-5 mb-2" />,
                    item: <li />,
                  }}
                />
              </div>
            )}

            <Button
              variant="link"
              className="[&>span]:p-0 min-w-0"
              onClick={() => setShowMore(!showMore)}>
              {showMore ? t('alert.delayed.showLess') : t('alert.delayed.showMore')}
            </Button>
          </>
        }
        variant="warning"
      />
    )
  }

  if (trackingPests?.some((pest) => REVIEW_PEST_STATUSES.includes(pest.status.slug))) {
    return (
      <Alert
        title={t('alert.review.title')}
        description={
          <Trans
            i18nKey={`${NS.TRACKING}:alert.review.description`}
            components={{ bold: <strong /> }}
          />
        }
        variant="tip"
      />
    )
  }

  return null
}
